import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Icon } from '../ui.jsx';
import { safeMediaPlay } from '../media-utils.js';
import { formatElapsed } from '../messaging-tools.js';
import { startCallTone, stopCallTone } from '../call-tones.js';

const QUALITY_LABEL = { excellent: 'Excellente', good: 'Bonne', weak: 'Moyenne', poor: 'Faible' };

function phaseLabel(phase, video) {
  const media = video ? 'vidéo' : 'audio';
  const labels = {
    outgoing: 'Appel en cours…',
    ringing: 'Ça sonne…',
    incoming: `Appel ${media} entrant`,
    connecting: 'Connexion sécurisée…',
    connected: `Appel ${media} chiffré`,
    failed: 'Appel impossible',
    ended: 'Appel terminé',
  };
  return labels[phase] || `Appel ${media}`;
}

function attachStream(element, stream, onBlocked) {
  if (!element) return () => {};
  element.srcObject = stream || null;
  if (stream) safeMediaPlay(element).then((ok) => onBlocked?.(!ok));
  return () => { try { element.srcObject = null; } catch (_) {} };
}

export default function AudioCallOverlay({
  call, onAccept, onDecline, onHangup, onToggleMute, onToggleVideo, onSwitchCamera, onDismiss, onLeaveVoiceMessage,
}) {
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [controlsVisible, setControlsVisible] = useState(true);
  const phase = call?.phase || 'idle';
  const connectedAt = call?.connectedAt || 0;
  const video = call?.mediaMode === 'video';

  useEffect(() => {
    if (!connectedAt) return undefined;
    setClockNow(Date.now());
    const timer = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [connectedAt]);

  // v4.9.3 : sonnerie locale. Le destinataire sonne tant que l'appel est
  // entrant ; l'appelant entend un retour de sonnerie uniquement après le
  // signal E2E `ringing`. Aucun média de sonnerie ne traverse le serveur.
  useEffect(() => {
    let stop = () => {};
    if (phase === 'incoming') stop = startCallTone('incoming');
    else if (phase === 'ringing' && call?.direction === 'outgoing') stop = startCallTone('outgoing');
    else stopCallTone();
    return () => stop();
  }, [phase, call?.direction, call?.callId]);

  useEffect(() => {
    if (!video || phase !== 'connected') { setControlsVisible(true); return undefined; }
    setControlsVisible(true);
    const timer = setTimeout(() => setControlsVisible(false), 4200);
    return () => clearTimeout(timer);
  }, [video, phase, call?.muted, call?.videoEnabled, call?.reconnecting]);

  useEffect(() => {
    if (video) return undefined;
    return attachStream(remoteAudioRef.current, call?.remoteStream, setNeedsPlay);
  }, [call?.remoteStream, video]);

  useEffect(() => {
    if (!video) return undefined;
    return attachStream(remoteVideoRef.current, call?.remoteStream, setNeedsPlay);
  }, [call?.remoteStream, video]);

  useEffect(() => {
    if (!video) return undefined;
    return attachStream(localVideoRef.current, call?.localStream, () => {});
  }, [call?.localStream, video]);

  const subtitle = phase === 'connected'
    ? formatElapsed(connectedAt, clockNow)
    : (call?.message || phaseLabel(phase, video));

  if (!call || phase === 'idle') return null;
  const incoming = phase === 'incoming';
  const terminal = phase === 'failed' || phase === 'ended';
  const canLeaveVoiceMessage = terminal && call?.direction === 'outgoing' && !call?.connectedAt
    && /aucune réponse|sans réponse/i.test(`${call?.message || ''} ${call?.error || ''}`);
  const route = call.networkRoute?.route === 'turn' ? 'TURN' : call.networkRoute?.route === 'p2p' ? 'P2P' : '';
  const quality = QUALITY_LABEL[call.quality?.level] || '';

  const retryPlay = () => {
    const media = video ? remoteVideoRef.current : remoteAudioRef.current;
    safeMediaPlay(media).then((ok) => setNeedsPlay(!ok));
  };

  return (
    <div className={`call-overlay ${video ? 'video-call-overlay' : ''}`} role="dialog" aria-modal="true" aria-label={video ? 'Appel vidéo' : 'Appel audio'}>
      {!video && <audio ref={remoteAudioRef} autoPlay playsInline />}
      {video && <div className="video-stage" onClick={() => phase === 'connected' && setControlsVisible((v) => !v)}>
        <video ref={remoteVideoRef} className="remote-call-video" autoPlay playsInline />
        {call.localStream && <video ref={localVideoRef} className={`local-call-video ${call.videoEnabled ? '' : 'camera-off'}`} autoPlay muted playsInline />}
        {!call.remoteStream && !terminal && <div className="video-waiting"><Avatar name={call.peer} /><span>{phaseLabel(phase, true)}</span></div>}
      </div>}
      {video && phase === 'connected' && <div className={`video-top-hud ${controlsVisible ? 'visible' : ''}`}>
        <strong>{call.peer}</strong><span>{subtitle}</span>
        {(quality || route) && <span className="video-top-diagnostics">{quality ? `Qualité ${quality.toLowerCase()}` : ''}{quality && route ? ' · ' : ''}{route}</span>}
      </div>}
      <div className={`call-card ${video ? 'video-call-card' : ''} ${video && phase === 'connected' ? 'video-connected' : ''} ${video && !controlsVisible && phase === 'connected' ? 'controls-hidden' : ''} ${call.reconnecting ? 'reconnecting' : ''}`}>
        <div className="call-security">🔒 {video ? 'Audio + vidéo' : 'Audio'} chiffré{video ? 's' : ''} de bout en bout</div>
        {call.verified && <div className="call-verified">✓ Contact vérifié</div>}
        {!video && <Avatar name={call.peer} />}
        <h1>{call.peer}</h1>
        <p className="call-status">{call.reconnecting ? 'Reconnexion sécurisée…' : phaseLabel(phase, video)}</p>
        {subtitle && <p className="muted">{subtitle}</p>}
        {phase === 'connected' && (quality || route) && (
          <div className="call-diagnostics" aria-label="Qualité de l’appel">
            {quality && <span className={`quality-pill ${call.quality.level}`}><i /> Qualité {quality.toLowerCase()}</span>}
            {route && <span className="route-pill">{route}</span>}
          </div>
        )}
        {call.error && <p className="error">{call.error}</p>}
        {needsPlay && <button type="button" className="ghost" onClick={retryPlay}>Activer le son</button>}
        <div className="call-actions">
          {incoming ? <>
            <button type="button" className="call-button decline" onClick={onDecline} aria-label="Refuser l’appel"><Icon.phoneDown /></button>
            <button type="button" className="call-button accept" onClick={onAccept} aria-label="Accepter l’appel"><Icon.phone /></button>
          </> : terminal ? <>
            {canLeaveVoiceMessage && <button type="button" className="call-button voicemail" onClick={onLeaveVoiceMessage} aria-label="Laisser un message vocal"><Icon.mic /></button>}
            <button type="button" className="call-button neutral" onClick={onDismiss} aria-label="Fermer">✕</button>
          </> : <>
            <button type="button" className={`call-button neutral ${call.muted ? 'active' : ''}`} onClick={onToggleMute} aria-label={call.muted ? 'Réactiver le micro' : 'Couper le micro'}>
              {call.muted ? <Icon.micOff /> : <Icon.mic />}
            </button>
            {video && <button type="button" className={`call-button neutral ${call.videoEnabled ? '' : 'active'}`} onClick={onToggleVideo} aria-label={call.videoEnabled ? 'Couper la caméra' : 'Réactiver la caméra'}>
              {call.videoEnabled ? <Icon.video /> : <Icon.videoOff />}
            </button>}
            {video && <button type="button" className="call-button neutral" onClick={onSwitchCamera} aria-label="Changer de caméra"><Icon.cameraFlip /></button>}
            <button type="button" className="call-button decline" onClick={onHangup} aria-label="Raccrocher"><Icon.phoneDown /></button>
          </>}
        </div>
        {!terminal && !incoming && <div className="call-action-labels">
          <span>{call.muted ? 'Micro coupé' : 'Micro'}</span>
          {video && <span>{call.videoEnabled ? 'Caméra' : 'Caméra coupée'}</span>}
          {video && <span>Retourner</span>}
          <span>Raccrocher</span>
        </div>}
        {terminal && canLeaveVoiceMessage && <div className="call-terminal-hint">🎙️ Laisser un message vocal</div>}
      </div>
    </div>
  );
}
